'use client'

import type { AuthChangeEvent, AuthResponse, AuthSession, AuthSubscription, AuthUser } from '@/lib/auth/types'

const AUTH_STORAGE_KEY = 'habitpack:auth-session'
const AUTH_USER_KEY = 'habitpack:auth-user'
const REFRESH_MARGIN_SECONDS = 60

type AuthListener = (event: AuthChangeEvent, session: AuthSession | null) => void

const listeners = new Set<AuthListener>()
let refreshPromise: Promise<AuthSession | null> | null = null
let persistedSessionRaw: string | null | undefined
let persistedSession: AuthSession | null = null
let liveSession: AuthSession | null = null

function getApiBaseUrl() {
  return import.meta.env.VITE_API_URL || 'http://localhost:3000/v1'
}

function isClient() {
  return typeof window !== 'undefined'
}

function safeParseSession(value: string | null): AuthSession | null {
  if (!value) return null

  try {
    const parsed = JSON.parse(value) as AuthSession
    if (!parsed?.user?.id) return null
    return parsed
  } catch {
    return null
  }
}

function syncCachedSession(value: string | null) {
  if (persistedSessionRaw === value) {
    return persistedSession
  }

  persistedSessionRaw = value
  persistedSession = safeParseSession(value)
  return persistedSession
}

function getSessionSnapshot() {
  return liveSession ?? persistedSession
}

function readStoredSession() {
  if (!isClient()) return null
  // Migrate: try new key first, fallback to legacy key
  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
  if (raw) {
    // Legacy localStorage session found — migrate by stripping tokens
    const session = syncCachedSession(raw)
    if (session) {
      writeStoredSession(session)
    }
    return getSessionSnapshot()
  }
  // New format: session without tokens stored under user key
  syncCachedSession(window.localStorage.getItem(AUTH_USER_KEY))
  return getSessionSnapshot()
}

function writeStoredSession(session: AuthSession | null) {
  if (!isClient()) return

  // Always clean up legacy key with tokens
  window.localStorage.removeItem(AUTH_STORAGE_KEY)

  if (!session) {
    liveSession = null
    syncCachedSession(null)
    window.localStorage.removeItem(AUTH_USER_KEY)
    return
  }

  liveSession = session

  // Store session metadata without sensitive tokens — tokens live in HttpOnly cookies
  const safeSession: AuthSession = {
    ...session,
    access_token: '',
    refresh_token: null,
  }

  const raw = JSON.stringify(safeSession)
  syncCachedSession(raw)
  window.localStorage.setItem(AUTH_USER_KEY, raw)
}

function emit(event: AuthChangeEvent, session: AuthSession | null) {
  for (const listener of listeners) {
    listener(event, session)
  }
}

async function requestAuth<T>(
  endpoint: string,
  options: RequestInit & {
    body?: unknown
    accessToken?: string | null
  } = {}
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers ?? {}),
  }

  if (options.accessToken) {
    ;(headers as Record<string, string>).Authorization = `Bearer ${options.accessToken}`
  }

  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  const contentType = response.headers.get('content-type') ?? ''
  const payload = contentType.includes('application/json') ? await response.json() : await response.text()

  if (!response.ok) {
    const message =
      typeof payload === 'string'
        ? payload
        : typeof payload?.message === 'string'
          ? payload.message
          : typeof payload?.error?.message === 'string'
            ? payload.error.message
            : `Auth request failed: ${response.status}`
    throw new Error(message)
  }

  return payload as T
}

function shouldRefresh(session: AuthSession | null) {
  if (!session) return false
  if (!session.access_token) return true
  // Tokens are now in HttpOnly cookies — check expiry from stored metadata
  if (!session.expires_at) return false
  const now = Math.floor(Date.now() / 1000)
  return session.expires_at - now <= REFRESH_MARGIN_SECONDS
}

function normalizeResponseSession(response: AuthResponse) {
  return response.session ?? null
}

async function refreshStoredSession(): Promise<AuthSession | null> {
  const currentSession = readStoredSession()
  if (!currentSession) return currentSession

  if (!refreshPromise) {
    // Refresh token is sent automatically via HttpOnly cookie
    refreshPromise = requestAuth<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: {},
    })
      .then((response) => {
        const nextSession = normalizeResponseSession(response)
        writeStoredSession(nextSession)
        emit('TOKEN_REFRESHED', nextSession)
        return nextSession
      })
      .catch((error) => {
        writeStoredSession(null)
        emit('SIGNED_OUT', null)
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

export async function getAuthSession(): Promise<AuthSession | null> {
  const currentSession = readStoredSession()
  if (!currentSession) {
    return null
  }
  if (!shouldRefresh(currentSession)) {
    return currentSession
  }
  return refreshStoredSession()
}

export function getAuthSessionSnapshot() {
  return readStoredSession()
}

/**
 * Verifies the stored session by forcing a token refresh against the server.
 * Supabase invalidates refresh tokens immediately when a user is deleted,
 * so this will correctly clear the local session for deleted accounts even
 * while a still-valid access token (JWT) is cached in localStorage.
 */
export async function verifySession(): Promise<AuthSession | null> {
  const currentSession = readStoredSession()
  if (!currentSession) return null

  try {
    return await refreshStoredSession()
  } catch {
    // refreshStoredSession already calls writeStoredSession(null) + emit('SIGNED_OUT')
    return null
  }
}

export function subscribeAuthStateChange(
  listener: AuthListener,
  emitInitialSession = false
): AuthSubscription {
  listeners.add(listener)

  const onStorage = (event: StorageEvent) => {
    if (event.key !== AUTH_USER_KEY && event.key !== AUTH_STORAGE_KEY) return
    const nextPersistedSession = syncCachedSession(event.newValue)

    if (!nextPersistedSession) {
      liveSession = null
    } else if (!liveSession || liveSession.user.id !== nextPersistedSession.user.id) {
      liveSession = nextPersistedSession
    }

    listener('TOKEN_REFRESHED', getSessionSnapshot())
  }

  if (isClient()) {
    window.addEventListener('storage', onStorage)
  }

  if (emitInitialSession) {
    queueMicrotask(() => {
      listener('INITIAL_SESSION', readStoredSession())
    })
  }

  return {
    unsubscribe() {
      listeners.delete(listener)
      if (isClient()) {
        window.removeEventListener('storage', onStorage)
      }
    },
  }
}

export async function getAccessToken() {
  const session = await getAuthSession()
  return session?.access_token ?? null
}

async function signInWithPassword(credentials: {
  email: string
  password: string
}): Promise<{ data: AuthResponse; error: null }> {
  const response = await requestAuth<AuthResponse>('/auth/login', {
    method: 'POST',
    body: credentials,
  })
  const session = normalizeResponseSession(response)
  writeStoredSession(session)
  emit('SIGNED_IN', session)
  return { data: response, error: null }
}

async function signUp(payload: {
  email: string
  password: string
  options?: {
    data?: Record<string, unknown>
  }
}): Promise<{ data: AuthResponse; error: null }> {
  const metadata = payload.options?.data ?? {}
  const response = await requestAuth<AuthResponse>('/auth/register', {
    method: 'POST',
    body: {
      email: payload.email,
      password: payload.password,
      username: typeof metadata.username === 'string' ? metadata.username : undefined,
      displayName:
        typeof metadata.display_name === 'string' ? metadata.display_name : undefined,
    },
  })
  const session = normalizeResponseSession(response)
  writeStoredSession(session)
  if (session) {
    emit('SIGNED_IN', session)
  }
  return { data: response, error: null }
}

async function signOut(): Promise<{ error: null }> {
  try {
    // Access token is sent automatically via HttpOnly cookie
    await requestAuth('/auth/logout', {
      method: 'POST',
    })
  } catch {
    // Session is local-first; keep logout resilient.
  }

  writeStoredSession(null)
  emit('SIGNED_OUT', null)
  return { error: null }
}

async function resetPasswordForEmail(email: string, options?: { redirectTo?: string }) {
  await requestAuth('/auth/password/reset', {
    method: 'POST',
    body: {
      email,
      redirectTo: options?.redirectTo,
    },
  })
  return { data: { success: true }, error: null }
}

async function updateUser(payload: {
  email?: string
  password?: string
  data?: Record<string, unknown>
}): Promise<{ data: { user: AuthUser; session: AuthSession | null }; error: null }> {
  const accessToken = await getAccessToken()
  const session = getAuthSessionSnapshot()
  if (!accessToken || !session) {
    throw new Error('Authentication required')
  }

  const response = await requestAuth<AuthResponse>('/auth/user', {
    method: 'PATCH',
    accessToken,
    body: payload,
  })

  const nextSession = response.session
    ? response.session
    : {
        ...session,
        user: response.user,
      }

  writeStoredSession(nextSession)
  emit('USER_UPDATED', nextSession)

  return { data: response, error: null }
}

export function createAuthClient() {
  return {
    auth: {
      async getSession() {
        return { data: { session: await getAuthSession() }, error: null as null }
      },
      async getUser() {
        return { data: { session: await verifySession() }, error: null as null }
      },
      onAuthStateChange(callback: (event: AuthChangeEvent, session: AuthSession | null) => void) {
        const subscription = subscribeAuthStateChange(callback, true)
        return { data: { subscription }, error: null as null }
      },
      signInWithPassword,
      signUp,
      signOut,
      resetPasswordForEmail,
      updateUser,
    },
  }
}
